import React, { useEffect, useState } from 'react';

import { useScheduleFerries } from 'Modules/StaffSchedule/Hooks/useScheduleFerries';
import { stringIsEmpty, stringNotEmpty } from 'Util/TypeGuards';
import { Tag } from 'Views/Components/_HumanWritten/CheckIn/FerryCheckIn/CheckInList/Tag';
import { CustomTooltip } from 'Views/Components/_HumanWritten/CustomTooltip/CustomTooltip';
import Icon from 'Views/Components/_HumanWritten/Icon/Icon';
import Popover from 'Views/Components/_HumanWritten/Popover';
import {
	ClearFiltersButton,
	countActiveFilters,
} from 'Views/Components/_HumanWritten/StaffTables/Shared/ClearFiltersButton';
import { StaffFiltersDatePicker } from 'Views/Components/_HumanWritten/StaffTables/StaffFiltersDatePicker';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';
import If from 'Views/Components/If/If';
import FhToggle from 'Views/Components/_HumanWritten/Toggle/FhToggle';
import {
	CHARTER_PERIODS,
	CharterPeriod,
	defaultFilters as defaultCharterFilters,
	useStaffChartersStore,
} from './StaffChartersFilterStore';
import { StaffChartersColumnSelector } from './StaffChartersColumnSelector';

export interface StaffCharterFiltersProps {
	setRefresh: () => void;
	/** On a customer's page: the customer column is not on offer, because it is not drawn. */
	hideCustomerColumn?: boolean;
	/**
	 * The reader is the customer rather than staff — see `StaffChartersListOuter`.
	 *
	 * Two of the filters stop making sense: a customer has no ferry list to filter by (the vessels
	 * come from `/api/admin`, which is staff-only), and they have no reserved charters, because a
	 * reserve is never shown to the customer named on it.
	 */
	viewerIsCustomer?: boolean;
}

/** The three states, in the colours the table's own status tags use. */
const STATUS_OPTIONS: { name: string; value: string; tagClassName: string }[] = [
	{ name: 'Reserved', value: 'Reserved', tagClassName: 'tag--warning' },
	{ name: 'Booked', value: 'Booked', tagClassName: 'tag--success' },
	{ name: 'Cancelled', value: 'Cancelled', tagClassName: 'tag--error' },
];

const arraysEqual = (a: string[], b: string[]) => {
	if (a.length !== b.length) return false;
	const sortedA = [...a].sort();
	const sortedB = [...b].sort();
	return sortedA.every((value, index) => value === sortedB[index]);
};

/**
 * The filter bar above the charters table, in both the places that table appears.
 *
 * Shaped like the parking reservations bar beside it, and for the same reasons: the multi-selects
 * hold a draft while their popover is open and commit it on close (so ticking four ferries is one
 * fetch, not four), and the search box debounces into the store.
 *
 * No export button. Nothing on this table is done to a set of charters, and there is no charters
 * export report behind one — the invoice report is what takes charters out to accounting.
 */
export function StaffCharterFilters({
	setRefresh,
	hideCustomerColumn,
	viewerIsCustomer = false,
}: StaffCharterFiltersProps) {
	const {
		filters,
		activeTab,
		setActiveTab,
		selectedStatuses,
		selectedFerries,
		setSelectedStatuses,
		setSelectedFerries,
		setSearchString,
		updateFilters,
		resetFilters,
	} = useStaffChartersStore();

	// Every vessel the operator has, not just the ones on the page — the same list the staff
	// schedule's ferry filter offers, sharing its cache entry.
	const ferries = useScheduleFerries(!viewerIsCustomer);

	// The states this reader can see. A customer is never shown a reserve, so offering it would be a
	// tick that always returns nothing — the server narrows to the same two either way.
	const statusOptions = viewerIsCustomer
		? STATUS_OPTIONS.filter(option => option.value !== 'Reserved')
		: STATUS_OPTIONS;

	const [tempSelectedStatuses, setTempSelectedStatuses] = useState<string[]>(selectedStatuses);
	const [tempSelectedFerries, setTempSelectedFerries] = useState<string[]>(selectedFerries);
	const [tempSearchString, setTempSearchString] = useState(() => filters.searchString || '');

	useEffect(() => {
		setTempSelectedStatuses(selectedStatuses);
	}, [selectedStatuses]);

	useEffect(() => {
		setTempSelectedFerries(selectedFerries);
	}, [selectedFerries]);

	// Typing goes into the store on a delay, so a search is one fetch rather than one per keystroke.
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			if (tempSearchString !== (filters.searchString || '')) {
				setSearchString(tempSearchString);
				setRefresh();
			}
		}, 500);

		return () => clearTimeout(timeoutId);
	}, [tempSearchString]);

	const onStatusAfterClose = () => {
		// Nothing ticked is a state the popover can be in but the filter cannot: an empty status list
		// means "every state" to the server, which is the opposite of what clearing every box looks
		// like. Put the previous selection back rather than silently widening the table.
		if (tempSelectedStatuses.length === 0) {
			setTempSelectedStatuses(selectedStatuses);
			return;
		}

		if (!arraysEqual(tempSelectedStatuses, selectedStatuses)) {
			setSelectedStatuses(tempSelectedStatuses);
			requestAnimationFrame(setRefresh);
		}
	};

	const onFerryAfterClose = () => {
		if (!arraysEqual(tempSelectedFerries, selectedFerries)) {
			setSelectedFerries(tempSelectedFerries);
			requestAnimationFrame(setRefresh);
		}
	};

	const getStatusDisplay = () => {
		if (selectedStatuses.length === 0 || selectedStatuses.length === statusOptions.length) {
			return 'All';
		}

		return selectedStatuses
			.map(s => statusOptions.find(opt => opt.value === s))
			.filter(Boolean)
			.map(opt => <Tag key={opt!.value} text={opt!.name} tagClassName={opt!.tagClassName} />);
	};

	const getFerryDisplay = () => {
		// An empty ferry filter is every ferry — the store keeps "no filter" as the empty list, so
		// there is no separate all-ferries value to check for.
		if (selectedFerries.length === 0 || selectedFerries.length === ferries.length) {
			return 'All';
		}

		return selectedFerries
			.map(id => ferries.find(f => f.id === id))
			.filter(Boolean)
			.map(ferry => <Tag key={ferry!.id} text={ferry!.name} tagClassName="grey" />);
	};

	const isAllStatusesSelected = tempSelectedStatuses.length === statusOptions.length;
	const isAllFerriesSelected =
		tempSelectedFerries.length === 0 || (ferries.length > 0 && tempSelectedFerries.length === ferries.length);

	return (
		<div className="table-filters__container">
			<div className="left">
				{/*
				 * Which stretch of the operator's charters to show, in the place the bookings table
				 * keeps All / Active / Past-Cancelled.
				 *
				 * The table opens on Upcoming, which is right for an operator looking at what the ferry
				 * is about to do — but before this the only way back to a finished charter was to know
				 * that an unnamed date filter was floored at this morning and to clear it by hand. It
				 * moves the range the picker beside it shows, so the two never disagree.
				 */}
				<FhToggle
					options={CHARTER_PERIODS}
					active={activeTab}
					onChange={(next: string) => {
						setActiveTab(next as CharterPeriod);
						setRefresh();
					}}
				/>

				<StaffFiltersDatePicker
					options={['Created Date', 'Charter Date']}
					startDate={filters.startDate}
					endDate={filters.endDate}
					filterByCreatedDate={filters.dateFilterType === 'created'}
					// Charters are taken months ahead, so the range has to reach into the future — a
					// picker capped at today would hide everything the table is mostly about.
					allowFutureDates
					onDateChange={(startDate, endDate) => {
						updateFilters({ startDate, endDate, pageNo: 0 });
						setRefresh();
					}}
					onFilterTypeChange={isCreatedDate => {
						updateFilters({ dateFilterType: isCreatedDate ? 'created' : 'charter', pageNo: 0 });
						setRefresh();
					}}
				/>

				<Popover
					popoverClassName="fh-popover"
					onAfterClose={onStatusAfterClose}
					buttonContent={
						<>
							<p className="grey">Status</p>
							<span className="tags">{getStatusDisplay()}</span>
						</>
					}
					popoverContent={() => (
						<div>
							<Checkbox
								className="fh-popover__checkbox fh-popover__list-item"
								model={{ value: isAllStatusesSelected }}
								modelProperty="value"
								label="All"
								onChecked={(_, value) =>
									setTempSelectedStatuses(value ? statusOptions.map(opt => opt.value) : [])
								}
							/>
							{statusOptions.map(statusOption => (
								<Checkbox
									className="fh-popover__checkbox fh-popover__list-item"
									key={statusOption.value}
									model={{ value: tempSelectedStatuses.includes(statusOption.value) }}
									modelProperty="value"
									label={statusOption.name}
									onChecked={(_, value) =>
										setTempSelectedStatuses(
											value
												? [...tempSelectedStatuses, statusOption.value]
												: tempSelectedStatuses.filter(x => x !== statusOption.value),
										)
									}
								/>
							))}
						</div>
					)}
				/>

				{/* One vessel means nothing to filter by — the whole table is that vessel. */}
				<If condition={ferries.length > 1}>
					<Popover
						popoverClassName="fh-popover"
						onAfterClose={onFerryAfterClose}
						buttonContent={
							<>
								<p className="grey">Ferry</p>
								<span className="tags">{getFerryDisplay()}</span>
							</>
						}
						popoverContent={() => (
							<div>
								<Checkbox
									className="fh-popover__checkbox fh-popover__list-item"
									model={{ value: isAllFerriesSelected }}
									modelProperty="value"
									label="All"
									onChecked={(_, value) =>
										// "All" is stored as no filter at all, so ticking it clears the
										// list rather than filling it with every id.
										setTempSelectedFerries(value ? [] : ferries.map(f => f.id))
									}
								/>
								{ferries.map(ferry => (
									<Checkbox
										className="fh-popover__checkbox fh-popover__list-item"
										key={ferry.id}
										model={{ value: tempSelectedFerries.includes(ferry.id) }}
										modelProperty="value"
										label={ferry.name}
										onChecked={(_, value) =>
											setTempSelectedFerries(
												value
													? [...tempSelectedFerries, ferry.id]
													: tempSelectedFerries.filter(x => x !== ferry.id),
											)
										}
									/>
								))}
							</div>
						)}
					/>
				</If>

				<form onSubmit={e => e.preventDefault()}>
					<div className="search-box">
						<input
							id="staff-charters-search-input"
							placeholder="Search"
							className="fh-basic-search"
							autoComplete="off"
							value={tempSearchString}
							onChange={e => setTempSearchString(e.target.value)}
							data-test-id="staff-charters-search"
						/>
						<If condition={stringIsEmpty(tempSearchString)}>
							<CustomTooltip
								id="staff-charters-search-tool-tip"
								content={
									viewerIsCustomer
										? 'Search by Charter ID, Charter Name, Ferry, Invoice Number'
										: 'Search by Charter ID, Charter Name, Ferry, Customer Name, Email, Company, Invoice Number'
								}
								toolTipPosition="bottom-right"
							/>
						</If>
						<If condition={stringNotEmpty(tempSearchString)}>
							<Button
								className="clear-search"
								onClick={e => {
									e.preventDefault();
									setTempSearchString('');
								}}
								colors={Colors.Black}
								display={Display.Text}
							>
								<Icon name="cross" />
							</Button>
						</If>
					</div>
				</form>

				<ClearFiltersButton
					activeCount={countActiveFilters(filters, defaultCharterFilters)}
					onClear={() => {
						resetFilters();
						setTempSearchString('');
						setRefresh();
					}}
					dataTestId="staff-charters-clear-filters"
				/>
			</div>
			<div className="right">
				<StaffChartersColumnSelector hideCustomerColumn={hideCustomerColumn} />
			</div>
		</div>
	);
}

export default StaffCharterFilters;
